import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { ModalForm, ProFormText, ProTable } from '@ant-design/pro-components';
import { App, Button, Popconfirm, Tag } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import {
  createConnection,
  FEISHU_BITABLE_CONNECTION_TYPE,
  FEISHU_CONTACT_CONNECTION_TYPE,
  HOSE_CONNECTION_TYPE,
  queryConnections,
  removeConnection,
  testFeishuConnection,
  testFeishuContactConnection,
  testHoseConnection,
  updateConnection,
} from '../services/collect';
import { getCollectionAgentDefinition } from '../utils/collection-agent';

const TEST_CONNECTIONS = {
  [FEISHU_BITABLE_CONNECTION_TYPE]: testFeishuConnection,
  [FEISHU_CONTACT_CONNECTION_TYPE]: testFeishuContactConnection,
  [HOSE_CONNECTION_TYPE]: testHoseConnection,
};

const ConnectionForm = ({
  connectionType,
  organizationId,
  platformLabel,
  record,
  trigger,
  onSaved,
}) => {
  const editing = Boolean(record);
  return (
    <ModalForm
      title={`${editing ? '修改' : '创建'}${platformLabel}连接`}
      trigger={trigger}
      width={520}
      initialValues={{ name: record?.name }}
      modalProps={{ destroyOnHidden: true }}
      onFinish={async ({ appId, appSecret, ...values }) => {
        const credentials = appId && appSecret ? { appId, appSecret } : undefined;
        await (editing ? updateConnection : createConnection)({
          data: { ...values, connectionType, credentials, id: record?.id, organizationId },
        });
        onSaved();
        return true;
      }}
    >
      <ProFormText name="name" label="连接名称" rules={[{ required: true }]} />
      <ProFormText name="appId" label="App ID" rules={editing ? [] : [{ required: true }]} />
      <ProFormText.Password
        name="appSecret"
        label="App Secret"
        extra={editing ? '留空表示不修改已保存的凭据' : undefined}
        rules={editing ? [] : [{ required: true }]}
      />
    </ModalForm>
  );
};

ConnectionForm.propTypes = {
  connectionType: PropTypes.string.isRequired,
  onSaved: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  platformLabel: PropTypes.string.isRequired,
  record: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  trigger: PropTypes.node.isRequired,
};

const AgentConnectionConfig = ({ agent, organizationId }) => {
  const connectionActionRef = useRef(null);
  const { message } = App.useApp();
  const definition = getCollectionAgentDefinition(agent.configuration);
  const reload = () => connectionActionRef.current?.reload?.();

  const connectionColumns = useMemo(
    () => [
      { title: '连接名称', dataIndex: 'name' },
      {
        title: '凭据',
        dataIndex: 'credentialConfigured',
        render: (_, row) =>
          row.credentialConfigured ? <Tag color="success">已配置</Tag> : <Tag>未配置</Tag>,
      },
      { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime' },
      {
        title: '操作',
        valueType: 'option',
        render: (_, row) => [
          <Button
            key="test"
            type="link"
            icon={<LinkOutlined />}
            onClick={async () => {
              await TEST_CONNECTIONS[definition.connectionType]({
                connectionId: row.id,
                organizationId,
              });
              message.success(`${definition.platformLabel}连接正常`);
            }}
          >
            测试
          </Button>,
          <ConnectionForm
            key="edit"
            connectionType={agent.configuration.connectionType}
            organizationId={organizationId}
            platformLabel={definition.platformLabel}
            record={row}
            onSaved={reload}
            trigger={
              <Button type="link" icon={<EditOutlined />}>
                修改
              </Button>
            }
          />,
          <Popconfirm
            key="delete"
            title="删除连接"
            onConfirm={async () => {
              await removeConnection({ data: { id: row.id, organizationId } });
              message.success('连接已删除');
              reload();
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>,
        ],
      },
    ],
    [agent.configuration.connectionType, definition, message, organizationId],
  );

  return (
    <ProTable
      actionRef={connectionActionRef}
      rowKey="id"
      size="small"
      options={false}
      columns={connectionColumns}
      search={false}
      request={(params) =>
        queryConnections({
          ...params,
          connectionType: agent.configuration.connectionType,
          organizationId,
        })
      }
      toolBarRender={() => [
        <ConnectionForm
          key="create"
          connectionType={agent.configuration.connectionType}
          organizationId={organizationId}
          platformLabel={definition.platformLabel}
          onSaved={reload}
          trigger={
            <Button type="primary" icon={<PlusOutlined />}>
              创建{definition.connectionLabel}
            </Button>
          }
        />,
      ]}
    />
  );
};

AgentConnectionConfig.propTypes = {
  agent: PropTypes.shape({
    configuration: PropTypes.shape({
      connectionType: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  organizationId: PropTypes.string.isRequired,
};

export default AgentConnectionConfig;
